import * as React from 'react';

import styled from 'styled-components';

import { FONT_SIZE, RADIUS, SPACING } from '../../packages/constants';
import { Article } from './atoms/article/article';
import { StyledNewReleaseNoData } from './atoms/images/images';

export interface ArticleData {
    releaseDate: string;
    isArchived: boolean;
    learnMoreLink?: string;
    isDraft: boolean;
    name: string;
    slug: string;
    videoLink?: MediaLink;
    videoThumbnails?: Thumbnail;
    thumbnailImage?: Thumbnail;
    productLabels: string;
    lastUpdated: string;
    createdOn: string;
    lastPublished: string;
    postSummary: string;
    id: string;
    product: string;
    cmsLocaleId: string;
}

interface MediaLink {
    url: string;
    id: string;
}

interface Thumbnail {
    fileId: string;
    url: string;
    alt: string;
}

export interface ReleaseNotesProps {
    /**
     * List of articles to display in the release notes.
     */
    articles: ArticleData[];
    /**
     * URL to open in a new tab.
     */
    url?: string;
    /**
     * Label for the URL.
     */
    urlDisplayText?: string;
    /**
     * Number of articles to display.
     */
    displaySize?: number;
    /**
     * Show videos and images
     */
    showMedia?: boolean;
    /**
     * External function to dispatch actions to.
     */
    handleAction?: <TType, TPayload, TResponse>(action: Action<TType, TPayload>) => Promise<void | TResponse>;
}

interface Action<TType, TPayload> {
    type: TType;
    payload?: TPayload;
}

/**
 * Actions dispatched to an external handler
 */
export enum ReleaseNotesExternalActions {
    OPEN_VIDEO = 'OPEN_VIDEO',
}

const StyledReleaseNotes = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const ArticleContainer = styled.div`
    margin: 5px;
    padding: ${SPACING.LARGE};
    border-radius: ${RADIUS.DEFAULT};
    border-style: solid;
    border-width: 2px;
    box-sizing: border-box;
    width: 100%;
    background-color: #ffffff;
    border-color: transparent;
    box-shadow: 0 1px 5px rgba(4, 37, 91, 0.25);
`;

const spotlightBrandColor = '#04255b';
const Link = styled.a`
    position: relative;
    text-decoration: none;
    text-transform: uppercase;
    display: inline-block;
    text-align: center;
    line-height: 2;
    margin-top: ${SPACING.XX_LARGE};
    font-size: ${FONT_SIZE.LARGE};
    font-weight: 500;
    padding: 0 1px;
    color: ${spotlightBrandColor};
    &::before,
    &::after {
        content: '';
        position: absolute;
        background-color: ${spotlightBrandColor};
        z-index: 1;
        height: 5%;
        width: 100%;
        left: 100%;
        bottom: 0;
    }
    &::before {
        width: 0%;
        left: 0;
        bottom: 0;
    }
    &::after {
        width: 100%;
        left: 0;
        bottom: 0;
    }
`;

const NoNewReleasesImage = styled.div`
    max-width: 420px;
    height: auto;
`;

const ReleaseNotes: React.FC<React.PropsWithChildren<ReleaseNotesProps>> = ({
    displaySize,
    articles,
    url,
    urlDisplayText,
    showMedia,
    handleAction,
    ...props
}) => {
    const activeArticles = articles.filter((a) => !a.isArchived && !a.isDraft);
    const itemsToDisplay = displaySize && displaySize < activeArticles.length ? activeArticles.slice(0, displaySize) : activeArticles;

    const handleOpenVideo = async (id: string, url: string) => {
        if (typeof handleAction !== 'function') {
            return;
        }

        await handleAction<ReleaseNotesExternalActions, { id: string; videoURL: string }, unknown>({
            type: ReleaseNotesExternalActions.OPEN_VIDEO,
            payload: {
                id: id,
                videoURL: url,
            },
        });
    };

    return (
        <StyledReleaseNotes {...props}>
            {itemsToDisplay.length > 0 ? (
                itemsToDisplay.map((article: ArticleData) => (
                    <ArticleContainer key={article.id}>
                        <Article data={article} showMedia={showMedia} handleOpenVideo={handleOpenVideo} />
                    </ArticleContainer>
                ))
            ) : (
                <NoNewReleasesImage>
                    <StyledNewReleaseNoData />
                </NoNewReleasesImage>
            )}
            {url && urlDisplayText && (
                <Link href={url} target={'_blank'}>
                    {urlDisplayText}
                </Link>
            )}
        </StyledReleaseNotes>
    );
};

export { ReleaseNotes };
